import { ProfileCard } from '@almbrand/profilecard';
import { formatAddress, mapContactDetails } from 'common/CustomerUtil';
import { HttpStatusCode } from 'constantsValues';
import { useThemeName } from 'hooks/useThemeName';
import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'store';
import { fetchCustomerDetails } from 'store/slices/customerSlice';
import { getStatusMessage } from 'utilities/statusMessageUtils';

export interface ProfileCardComponentProps {
	editTitle: string;
	editLink: Link;
	logOutTitle: string;
	logOutLink: Link;
	statusMessages?: {
		items: Array<{
			content: {
				statusMessage: string;
				statusCode: HttpStatusCode;
			};
		}>;
	};
}

export const ProfileCardComponent: React.FC<ProfileCardComponentProps> = ({
	editTitle,
	editLink,
	logOutTitle,
	logOutLink,
	statusMessages,
}) => {
	const themeName = useThemeName();
	const dispatch = useAppDispatch();
	const customerDetails = useAppSelector((state) => state.customer.data);
	const detailsStatus = useAppSelector((state) => state.customer.status);

	useEffect(() => {
		if (detailsStatus === HttpStatusCode.Idle) {
			dispatch(fetchCustomerDetails());
		}
	}, [detailsStatus, dispatch]);

	if (detailsStatus === HttpStatusCode.Idle || detailsStatus === HttpStatusCode.Loading) return null;

	const isError = detailsStatus === HttpStatusCode.InternalServerError || detailsStatus === HttpStatusCode.NoContent;

	const contactDetails = mapContactDetails(customerDetails?.contact?.contact_details ?? []);
	const fallback = 'Feltet kunne ikke indlæses.';

	// Map details to profileInfo, providing a fallback for missing values
	const getDetail = (type: string) => {
		const isMissing = !contactDetails[type];
		return { value: isMissing ? fallback : contactDetails[type], isMissing };
	};
	const rawAddress = formatAddress(contactDetails);
	const addressIsMissing = !rawAddress;

	return (
		<ProfileCard
			name={customerDetails?.organisation?.name || ''}
			profileInfo={[
				{ title: 'Email', ...getDetail('Email__c') },
				{ title: 'Telefon', ...getDetail('Mobile__c') },
				{ title: 'Adresse', value: addressIsMissing ? fallback : rawAddress, isMissing: addressIsMissing },
			]}
			editButtonProps={{
				children: editTitle ?? 'Rediger information',
				buttonType: 'link',
				linkButtonSettings: {
					href: editLink?.url,
				},
				iconProps: {
					icon: 'pen',
					themeName: themeName,
				},
				iconPosition: 'right',
			}}
			logoutButtonProps={{
				children: logOutTitle ?? 'Log ud',
				buttonType: 'link',
				linkButtonSettings: {
					href: logOutLink?.url,
				},
			}}
			showError={isError}
			errorMessage={getStatusMessage(statusMessages, detailsStatus)}
		/>
	);
};
